<div class="container">
  <div class="header">
    <h1>Admin Panel</h1>
    <p class="subtitle" *ngIf="!isAuthenticated">Please log in to access admin panel</p>
    <p class="subtitle" *ngIf="isAuthenticated && !isOwner">
      {{ accessMessage || 'Access denied: Owner only.' }}
    </p>
  </div>

  <div *ngIf="isAuthenticated && isOwner" class="admin-content">
    <div class="ops-alert" *ngIf="pricingOpsAlert">{{ pricingOpsAlert }}</div>
    <!-- Quick Links -->
    <div class="quick-links">
      <a routerLink="/admin/pricing" class="quick-link-card">
        <span class="link-icon"><mat-icon>attach_money</mat-icon></span>
        <span class="link-text">Pricing snapshot</span>
        <span class="link-desc">View bundled SDK pricing catalog</span>
      </a>
      <a routerLink="/admin/optimizer-lab" class="quick-link-card">
        <span class="link-icon"><mat-icon>analytics</mat-icon></span>
        <span class="link-text">Optimizer Lab</span>
        <span class="link-desc">Test optimization pipeline</span>
      </a>
        <a routerLink="/observe" class="quick-link-card">
          <span class="link-icon"><mat-icon>science</mat-icon></span>
          <span class="link-text">Observe (optimizer lab)</span>
          <span class="link-desc">Try optimization modes</span>
        </a>
    </div>
    
    <!-- Tabs -->
    <div class="tabs">
      <button 
        class="tab" 
        [class.active]="activeTab === 'orgs'"
        (click)="switchTab('orgs')">
        Organizations
      </button>
      <button 
        class="tab" 
        [class.active]="activeTab === 'users'"
        (click)="switchTab('users')">
        Users
      </button>
    </div>
    <!-- Organizations Tab -->
    <div class="tab-content" *ngIf="activeTab === 'orgs'">
      <!-- Org List -->
      <div class="section">
      <h2>Organizations</h2>
      <div class="loading" *ngIf="loading">
        <p>Loading organizations...</p>
      </div>
      <div class="error" *ngIf="orgsListError">{{ orgsListError }}</div>

      <div class="org-list" *ngIf="!loading && !orgsListError && orgs.length > 0">
        <div class="org-card" *ngFor="let org of orgs" [class.selected]="selectedOrg?.id === org.id">
          <div class="org-header" (click)="selectOrg(org.id)">
            <div class="org-info">
              <h3>{{ org.name }}</h3>
              <p class="org-id">ID: {{ org.id }}</p>
              <div class="org-meta">
                <span class="badge" [class.active]="org.subscription_status === 'active'"
                      [class.trial]="org.subscription_status === 'trial'">
                  {{ org.subscription_status }}
                </span>
                <span class="created">Created: {{ formatDate(org.created_at) }}</span>
              </div>
            </div>
            <div class="org-stats" *ngIf="org.stats">
              <div class="stat">
                <span class="stat-label">Projects:</span>
                <span class="stat-value">{{ org.stats.projects }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">API Keys:</span>
                <span class="stat-value">{{ org.stats.api_keys }}</span>
              </div>
              <div class="stat">
                <span class="stat-label">Runs:</span>
                <span class="stat-value">{{ org.stats.runs }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="empty empty-emphasis" *ngIf="!loading && !orgsListError && orgs.length === 0">
        <p><strong>No organizations yet</strong></p>
        <p class="empty-hint">When users create an org (sign-up flow), it will appear here.</p>
      </div>
    </div>

    <!-- Org Details -->
    <div class="section" *ngIf="selectedOrg">
      <h2>Organization Details</h2>
      <div class="loading" *ngIf="loadingDetails">
        <p>Loading details...</p>
      </div>
      <div class="error" *ngIf="orgDetailsError && !loadingDetails">{{ orgDetailsError }}</div>

      <div class="org-details" *ngIf="!loadingDetails && orgDetails">
        <div class="detail-group">
          <label>Name:</label>
          <div class="edit-name">
            <input
              type="text"
              [(ngModel)]="editName"
              class="name-input"
              *ngIf="editingName"
            />
            <span *ngIf="!editingName">{{ orgDetails.name }}</span>
            <div class="edit-actions" *ngIf="editingName">
              <button (click)="saveName()" [disabled]="saving" class="btn btn-sm btn-primary">
                {{ saving ? 'Saving...' : 'Save' }}
              </button>
              <button (click)="cancelEdit()" [disabled]="saving" class="btn btn-sm btn-secondary">
                Cancel
              </button>
            </div>
            <button (click)="startEdit()" *ngIf="!editingName" class="btn btn-sm btn-secondary">
              Edit
            </button>
          </div>
        </div>
        
        <div class="detail-group">
          <label>ID:</label>
          <span>{{ orgDetails.id }}</span>
        </div>
        
        <div class="detail-group">
          <label>Status:</label>
          <span class="badge" [class.active]="orgDetails.subscription_status === 'active'"
                [class.trial]="orgDetails.subscription_status === 'trial'">
            {{ orgDetails.subscription_status }}
          </span>
        </div>
        
        <div class="detail-group">
          <label>Trial Ends:</label>
          <span>{{ orgDetails.trial_ends_at ? formatDate(orgDetails.trial_ends_at) : 'N/A' }}</span>
        </div>
        
        <div class="detail-group">
          <label>Created:</label>
          <span>{{ formatDate(orgDetails.created_at) }}</span>
        </div>

        <div class="detail-group">
          <label>SDK Access:</label>
          <div class="sdk-access-control">
            <span class="sdk-status" [class.enabled]="orgDetails.sdk_access_enabled" [class.disabled]="!orgDetails.sdk_access_enabled">
              {{ orgDetails.sdk_access_enabled ? '✅ Enabled' : '❌ Disabled' }}
            </span>
            <button 
              (click)="toggleSdkAccess(!orgDetails.sdk_access_enabled)" 
              [disabled]="togglingSdkAccess"
              class="btn btn-sm"
              [class.btn-primary]="!orgDetails.sdk_access_enabled"
              [class.btn-secondary]="orgDetails.sdk_access_enabled">
              {{ togglingSdkAccess ? 'Updating...' : (orgDetails.sdk_access_enabled ? 'Disable SDK' : 'Enable SDK') }}
            </button>
          </div>
          <p class="help-text">
            When disabled, users cannot use the SDK (agent endpoints will return 403).
            Chat optimization endpoints are not affected.
          </p>
        </div>

        <div class="detail-group" *ngIf="orgDetails.stats">
          <label>Statistics:</label>
          <div class="stats-grid">
            <div class="stat-item">
              <span class="stat-label">Projects:</span>
              <span class="stat-value">{{ orgDetails.stats.projects }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">API Keys:</span>
              <span class="stat-value">{{ orgDetails.stats.api_keys }}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Runs:</span>
              <span class="stat-value">{{ orgDetails.stats.runs }}</span>
            </div>
          </div>
        </div>

        <div class="detail-group" *ngIf="orgDetails.projects && orgDetails.projects.length > 0">
          <label>Projects:</label>
          <ul class="project-list">
            <li *ngFor="let project of orgDetails.projects">
              {{ project.name }} <span class="project-id">({{ project.id }})</span>
            </li>
          </ul>
        </div>

        <div class="detail-group" *ngIf="orgDetails.api_keys && orgDetails.api_keys.length > 0">
          <label>API Keys:</label>
          <ul class="api-key-list">
            <li *ngFor="let key of orgDetails.api_keys">
              <span>{{ key.name || 'Unnamed' }}</span>
              <span class="key-meta">
                Created: {{ formatDate(key.created_at) }}
                <span *ngIf="key.revoked_at" class="revoked">(Revoked)</span>
              </span>
            </li>
          </ul>
        </div>

        <div class="danger-zone">
          <h3>Danger Zone</h3>
          <p class="warning">Deleting an organization will permanently remove all associated data including projects, API keys, runs, and savings data. This action cannot be undone.</p>
          <button (click)="confirmDelete()" [disabled]="deleting" class="btn btn-danger">
            {{ deleting ? 'Deleting...' : 'Delete Organization' }}
          </button>
          <div class="delete-confirm" *ngIf="showDeleteConfirm">
            <p>Are you sure you want to delete "{{ orgDetails.name }}"?</p>
            <div class="confirm-actions">
              <button (click)="deleteOrg()" [disabled]="deleting" class="btn btn-danger">
                {{ deleting ? 'Deleting...' : 'Yes, Delete' }}
              </button>
              <button (click)="cancelDelete()" [disabled]="deleting" class="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>

    <!-- Users Tab -->
    <div class="tab-content" *ngIf="activeTab === 'users'">
      <div class="section">
        <h2>Users</h2>
        <p class="users-lead">
          All Supabase accounts (including users who have not joined an org yet).
          <strong>Pause</strong> stops Stripe collection and (after a 30-day grace) blocks write API access.
          <strong>Inactive</strong> keeps the app usable but locks real savings to Observe on orgs they own until they pay (does not auto-pause Stripe).
          <strong>Billing exempt</strong> (staff) gives full product access without a subscription on their first owned org.
          Delete removes app data and the auth user.
        </p>
        <div class="loading" *ngIf="loadingUsers">
          <p>Loading users...</p>
        </div>
        <div class="error" *ngIf="usersError">{{ usersError }}</div>
        
        <div class="user-list" *ngIf="!loadingUsers && !usersError && users.length > 0">
          <div class="user-card" *ngFor="let user of users">
            <div class="user-header">
              <div class="user-info">
                <h3>{{ user.email || user.user_id }}</h3>
                <p class="user-id">ID: {{ user.user_id }}</p>
                <span class="access-badge grace" *ngIf="isSavingsGrace(user)">Paused — full savings until {{ formatDate(user.pause_savings_until!) }}</span>
                <span class="access-badge" *ngIf="user.access_state === 'paused' && !isSavingsGrace(user)">Paused — API read-only (Observe)</span>
                <span class="access-badge inactive" *ngIf="user.access_state === 'inactive'">Inactive — Observe savings (real savings locked)</span>
                <span class="access-badge active" *ngIf="isAccessActive(user)">Active</span>
                <span class="access-badge exempt-org" *ngIf="user.primary_owner_org?.platform_exempt">Billing exempt (org)</span>
                <span class="platform-role-badge admin" *ngIf="user.platform_role === 'admin'">Admin</span>
                <span class="platform-role-badge superuser" *ngIf="user.platform_role === 'superuser'">Superuser</span>
                <span class="platform-role-badge exempt" *ngIf="user.platform_role === 'exempt'">Exempt</span>
              </div>
              <div class="user-actions">
                <button
                  type="button"
                  class="btn btn-sm"
                  [class.btn-secondary]="user.platform_role === 'admin'"
                  [class.btn-primary]="user.platform_role !== 'admin'"
                  *ngIf="canManageRoles && user.platform_role !== 'superuser'"
                  (click)="toggleAdmin(user)"
                  [disabled]="userActionBusy === user.user_id">
                  {{ user.platform_role === 'admin' ? 'Remove Admin' : 'Make Admin' }}
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  *ngIf="isAccessActive(user) || user.access_state === 'inactive'"
                  (click)="setUserAccess(user, 'paused')"
                  [disabled]="userActionBusy === user.user_id">
                  Pause
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  *ngIf="user.access_state === 'paused'"
                  (click)="setUserAccess(user, 'active')"
                  [disabled]="userActionBusy === user.user_id">
                  Reactivate
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  *ngIf="user.access_state !== 'inactive'"
                  (click)="setUserAccess(user, 'inactive')"
                  [disabled]="userActionBusy === user.user_id">
                  Set inactive
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  *ngIf="user.access_state === 'inactive'"
                  (click)="setUserAccess(user, 'active')"
                  [disabled]="userActionBusy === user.user_id">
                  Set active
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  *ngIf="canManageOwnerOrgBilling && user.primary_owner_org && !user.primary_owner_org.platform_exempt"
                  (click)="toggleOwnerOrgBilling(user, true)"
                  [disabled]="userActionBusy === user.user_id">
                  Billing exempt
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-secondary"
                  *ngIf="canManageOwnerOrgBilling && user.primary_owner_org?.platform_exempt"
                  (click)="toggleOwnerOrgBilling(user, false)"
                  [disabled]="userActionBusy === user.user_id">
                  Remove billing exempt
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-danger"
                  (click)="askDeleteUser(user)"
                  [disabled]="userActionBusy === user.user_id">
                  Delete user
                </button>
              </div>
            </div>
            <div class="delete-user-confirm" *ngIf="deleteConfirmUserId === user.user_id">
              <p>Permanently delete this user? App data is removed; sole-owner orgs are deleted entirely.</p>
              <div class="confirm-actions">
                <button type="button" class="btn btn-danger btn-sm" (click)="deleteUser(user)" [disabled]="userActionBusy === user.user_id">
                  {{ userActionBusy === user.user_id ? 'Deleting…' : 'Yes, delete' }}
                </button>
                <button type="button" class="btn btn-secondary btn-sm" (click)="cancelDeleteUser()" [disabled]="userActionBusy === user.user_id">Cancel</button>
              </div>
            </div>
            <div class="user-orgs" *ngIf="(user.orgs?.length ?? 0) > 0">
              <h4>Organizations:</h4>
              <ul>
                <li *ngFor="let org of user.orgs ?? []">
                  <strong>{{ org.org_name }}</strong> 
                  <span class="role-badge">{{ org.role }}</span>
                  <span class="org-date">Joined: {{ formatDate(org.created_at) }}</span>
                </li>
              </ul>
            </div>
            <div class="user-orgs" *ngIf="(user.orgs?.length ?? 0) === 0">
              <p class="no-orgs">No organizations</p>
            </div>
          </div>
        </div>
        
        <div class="empty empty-emphasis" *ngIf="!loadingUsers && !usersError && users.length === 0">
          <p><strong>No users in the directory</strong></p>
          <p class="empty-hint">Supabase accounts will show here after they exist (including users who have not joined an org yet).</p>
        </div>
      </div>
    </div>
  </div>
</div>
